// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file 
 * @author James Reid
 */

// @ts-check

// @@imports-dependencies
import { ChevronDown, createElement } from "lucide"

// @@body
/**
 * @template T
 */
class ListSelect extends HTMLElement {
    #select = document.createElement("select")
    #options = /** @type {HTMLLIElement[]} */ ([])

    /**
     *
     */
    constructor() {
        super()

        this.value = ""
        this.timeOpened = null

        this.querySelectorAll("div.dropdown ul li")
            .forEach(option => {
                if (!(option instanceof HTMLLIElement)) { return }
                this.#select.append(this.#getOptionElement(option))
                this.#options.push(option)

                option.addEventListener("click", () => {
                    this.value = option.dataset.value || ""
                })

                if (option.ariaSelected) {
                    this.value = option.dataset.value || ""
                    option.ariaSelected = null
                }
            })
    }

    /**
     * 
     */
    connectedCallback() {
        // Fetch hidden select element attributes from custom component dataset
        // attributes.
        this.#select.id = this.dataset.id || ""
        this.#select.name = this.dataset.name || ""
        this.#select.hidden = true

        // Mount hidden select element which will hold the state of custom
        // dropdown.
        this.append(this.#select)

        // Add label element if required data attribute is provided.
        if (this.dataset.label) {
            const label = document.createElement("label")
            label.htmlFor = this.#select.id
            label.innerHTML = this.dataset.label
            this.insertBefore(label, this.firstChild)
        }

        this.querySelector("div.dropdown")?.append(createElement(ChevronDown))

        this.querySelector("div.dropdown")?.addEventListener("click", () => {
            this.toggle()
        })

        document.addEventListener("click", () => {
            if (Date.now() - this.timeOpened > 100) { this.close() }
        })
    }

    toggle() {
        this.querySelector("div.dropdown ul")?.classList.toggle("closed")
        this.timeOpened = Date.now()
    }

    open() {
        this.querySelector("div.dropdown ul")?.classList.remove("closed")
        this.timeOpened = Date.now()
    }

    close() {
        this.querySelector("div.dropdown ul")?.classList.add("closed")
    }

    /**
     * @param {HTMLLIElement} option
     * @returns {HTMLOptionElement}
     */
    #getOptionElement(option) {
        const element = document.createElement("option")
        element.value = option.dataset.value || ""
        element.innerHTML = this.#getOptionName(option)

        return element
    }

    /**
     * @param {HTMLLIElement} option
     * @returns {string}
     */
    #getOptionName(option) {
        return option.dataset.displayName ||
            option.querySelector("h4")?.innerHTML ||
            option.querySelector("p.description.short")?.innerHTML ||
            ""
    }

    /**
     * @param {string} name
     */
    #setSelectedName(name) {
        const selectedName = this.querySelector("div.dropdown p")
        if (selectedName) { selectedName.innerHTML = name }
    }

    /**
     * @returns {T}
     */
    get value() {
        return this.#select.value
    }

    /**
     * @param {T}
     */
    set value(value) {
        if (value === "") {
            this.#setSelectedName("Select option")
            this.#select.value = value
        }

        for (const option of this.#options) {
            if (option.dataset.value === value) {
                this.#setSelectedName(this.#getOptionName(option))

                this.#select.value = value
                const event = new Event("change")
                this.dispatchEvent(event)

                break
            }
        }
    }

    /**
     * 
     * @param {ListSelect} listSelect
     * @param {string} [value]
     */
    static preConnectValue(listSelect, value) {
        const options = listSelect.querySelectorAll("div.dropdown ul li")

        for (const option of options) {
            if (!(option instanceof HTMLLIElement)) { continue }
            if (value && option.dataset.value === value) {
                option.ariaSelected = "true"
            }
            else if (value) { option.ariaSelected = null }
            else if (option.ariaSelected === "true") {
                value = option.dataset.value
                break
            }
        }

        return value
    }

    static get customName() {
        return "list-select"
    }
}

// @@exports
export { ListSelect }
